import React, { useMemo } from 'react';
import {
  ANCESTRY_REGIONS_BY_PARENT,
  type AncestryRegionByParentSeg,
} from '../data/ancestryRegionsByParent';

export type { AncestryRegionByParentSeg };

export interface ChromosomePainterAncestryRegionLayerProps {
  chrom: string;
  chromLength: number;
  side: 'Paternal' | 'Maternal';
  enabled: boolean;
  hovered: AncestryRegionByParentSeg | null;
  setHovered: (seg: AncestryRegionByParentSeg | null) => void;
}

/**
 * Underlay for chromosome bars: AncestryDNA-style regions assigned to maternal vs paternal copies.
 * Renders beneath progenitor / DNA Painter segments (z-index 0).
 */
const ChromosomePainterAncestryRegionLayer: React.FC<ChromosomePainterAncestryRegionLayerProps> = ({
  chrom,
  chromLength,
  side,
  enabled,
  hovered,
  setHovered,
}) => {
  const segs = useMemo(() => {
    if (!enabled) return [];
    return ANCESTRY_REGIONS_BY_PARENT.filter(
      (s) => String(s.chromosome) === chrom && s.side === side
    );
  }, [enabled, chrom, side]);

  if (!enabled || segs.length === 0) return null;

  const len = chromLength;
  const showTip =
    hovered &&
    String(hovered.chromosome) === chrom &&
    hovered.side === side &&
    segs.some((s) => s.id === hovered.id);

  return (
    <>
      {segs.map((seg) => {
        const left = (seg.start / len) * 100;
        const width = ((seg.end - seg.start) / len) * 100;
        const isHov = hovered?.id === seg.id;
        return (
          <div
            key={seg.id}
            className="chromosome-painter-ancestry-region-by-parent-segment"
            style={{
              position: 'absolute',
              left: `${left}%`,
              width: `${Math.max(width, 0.2)}%`,
              top: 0,
              height: '100%',
              background: seg.color,
              borderRadius: 6,
              opacity: isHov ? 0.62 : 0.4,
              zIndex: 0,
              cursor: 'help',
              boxSizing: 'border-box',
              border: '1px solid rgba(0,0,0,0.25)',
            }}
            onMouseEnter={() => setHovered(seg)}
            onMouseLeave={() => setHovered(null)}
            title={`${seg.group} (${seg.side})`}
          />
        );
      })}
      {showTip && hovered ? (
        <div
          className="genealogy-card chromosome-painter-ancestry-region-by-parent-tooltip"
          style={{
            position: 'absolute',
            left: `${(hovered.start / len) * 100}%`,
            top: 22,
            color: '#222',
            borderRadius: 6,
            padding: '8px 14px',
            fontSize: 14,
            boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
            zIndex: 12,
            minWidth: 240,
            maxWidth: 360,
            background: '#fff',
          }}
        >
          <div>
            <strong>Ancestry region</strong> ({hovered.side})
          </div>
          <div style={{ color: '#444', marginTop: 4 }}>{hovered.group}</div>
          <div style={{ marginTop: 6 }}>
            <strong>Chr {hovered.chromosome}:</strong> {hovered.start.toLocaleString()} –{' '}
            {hovered.end.toLocaleString()}
          </div>
        </div>
      ) : null}
    </>
  );
};

export default ChromosomePainterAncestryRegionLayer;
